/**
 * ForYouPage - Phase 2.1: AI Hero UI + Paywall Gating + Phase 5.0 Controls
 *
 * Implements:
 * - Fetch from GET /api/for-you (Phase 2.0 backend cache)
 * - AI badge for Pro users when hero.isAiGenerated === true
 * - Paywall blur for free users when hero.isAiGenerated === true
 * - Fallback to Phase 1 algorithmic shelves on API error/missing hero
 * - NO LLM calls from frontend
 * - Phase 5.0: Hide controls (movie/actor/director), Taste Controls, Why this?
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePersistFlush } from '../components/QueryCachePersister';
import {
  ArrowLeft,
  Play,
  Plus,
  Info,
  ChevronLeft,
  ChevronRight,
  Lock,
  Sparkles,
  Star,
  Wand2,
  EyeOff,
  HelpCircle,
  SlidersHorizontal,
  X,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useMediaType } from '../contexts/MediaTypeContext';
import { useUserLibrary } from '../contexts/UserLibraryContext';
import { useChat } from '../contexts/ChatContext';
import { MovieCard } from '../components/MovieCard';
import { useForYouShelves, type ForYouShelf } from '../hooks/useForYouShelves';
import { useBackNavigation } from '../hooks/useBackNavigation';
import { queryKeys } from '../lib/queryKeys';
import { apiFetch } from '../lib/api';
import { effectiveIsPro, effectiveTier } from '../utils/entitlements';
import { UNIFIED_GENRES } from '../utils/constants';
import type { MediaItem, MediaType } from '../types/movie';

// =============================================================================
// API RESPONSE TYPES (matching worker/lib/for-you.js)
// =============================================================================

interface ForYouHeroRecommendation {
  id: number;
  title: string;
  poster_path: string | null;
  backdrop_path: string | null;
  release_date: string | null;
  vote_average: number;
  media_type: 'movie' | 'tv';
  filmpl_rating?: number | null;
  imdb_rating?: number | null;
  rt_rating?: number | null;
  popularity?: number;
  // Metadata fields for parity with Discover
  runtime?: number | null;
  genre_ids?: number[];
  vote_count?: number | null;
}

interface ForYouHero {
  type: 'algorithmic' | 'cold_start' | 'personalized';
  recommendation: ForYouHeroRecommendation;
  reason: string | null;
  isAiGenerated: boolean;
  generatedAt: string;
  payloadVersion: string;
}

interface ForYouShelfApi {
  id: string;
  title: string;
  subtitle?: string;
  items: ForYouHeroRecommendation[];
  anchor?: { id: number; title: string };
  isPro: boolean;
  isSocial?: boolean;
  isAiGenerated: boolean;
  /** Shelf state: 'populated' (4+ items), 'needs_more' (1-3 items), 'empty' (0 items) */
  state?: 'populated' | 'needs_more' | 'empty';
  /** Message to show when shelf is empty or needs more items */
  empty_message?: string | null;
}

interface ForYouApiResponse {
  hero: ForYouHero | null;
  shelves: ForYouShelfApi[];
  isColdStart: boolean;
  cacheHit: boolean;
  generatedAt: string;
  payloadVersion: string;
  fallbackReason?: string;
}

// =============================================================================
// EXTENDED SHELF TYPE
// =============================================================================

interface ForYouShelfWithPro extends ForYouShelf {
  isPro?: boolean;
  isSocial?: boolean;
  /** Shelf state: 'populated' (4+ items), 'needs_more' (1-3 items), 'empty' (0 items) */
  state?: 'populated' | 'needs_more' | 'empty';
  /** Message to show when shelf is empty or needs more items */
  empty_message?: string | null;
}

// =============================================================================
// CREDITS TYPES (from detail endpoint)
// =============================================================================

interface CreditsPerson {
  id: number;
  name: string;
  character?: string;
  job?: string;
  department?: string;
  profile_path: string | null;
  order?: number;
}

interface CreditsResponse {
  credits: {
    cast: CreditsPerson[];
    crew: CreditsPerson[];
  };
}

// =============================================================================
// TASTE PREFERENCES TYPES (matching worker/lib/tune.js)
// =============================================================================

interface TastePreferences {
  intensity?: number; // 1-10
  popularity?: number; // 1-10
  runtime?: { min: number; max: number }; // minutes
  era?: { min: number; max: number }; // years
  genreBoosts?: number[];
  genreDemotes?: number[];
}

// =============================================================================
// HELPER: Fetch credits for director/actor IDs
// =============================================================================

async function fetchCreditsForHero(
  mediaType: 'movie' | 'tv',
  mediaId: number,
  token?: string
): Promise<{ director: CreditsPerson | null; topCast: CreditsPerson[] }> {
  try {
    const data = await apiFetch<CreditsResponse>(`/${mediaType}/${mediaId}`, { token });
    const director = data.credits?.crew?.find((c) => c.job === 'Director') || null;
    const topCast = data.credits?.cast?.slice(0, 5) || [];
    return { director, topCast };
  } catch (e) {
    console.error('Failed to fetch credits:', e);
    return { director: null, topCast: [] };
  }
}

// =============================================================================
// HELPER: Spotify-grade preference filtering and reranking
// Phase 5.1: Hard filters + dominant boosts + baseScore sorting
// =============================================================================

/**
 * Compute normalized base score (0-100 scale) for stable sorting.
 * Uses fallback chain: filmpl_rating > imdb_rating*10 > rt_rating > vote_average*10 > 0
 */
function computeBaseScore(movie: MediaItem): number {
  if (movie.filmpl_rating != null) {
    return movie.filmpl_rating * 10; // filmpl is 0-10, scale to 0-100
  }
  if (movie.imdb_rating != null) {
    return movie.imdb_rating * 10; // imdb is 0-10, scale to 0-100
  }
  if (movie.rt_rating != null) {
    return movie.rt_rating; // RT is already 0-100
  }
  if (movie.vote_average != null) {
    return movie.vote_average * 10; // TMDB vote_average is 0-10, scale to 0-100
  }
  return 0;
}

/**
 * Check if item passes HARD preference filters.
 * Items that fail hard filters are REMOVED, not just demoted.
 *
 * Hard filters:
 * - Runtime: If prefs.runtime set AND movie.runtime exists, must be within [min, max]
 * - Genre demotes: If ANY of movie's genres are in prefs.genreDemotes, FILTER OUT
 */
function passesHardPrefs(movie: MediaItem, prefs: TastePreferences): boolean {
  // Runtime hard filter (only applies when runtime exists on item)
  if (prefs.runtime && movie.runtime != null) {
    if (movie.runtime < prefs.runtime.min || movie.runtime > prefs.runtime.max) {
      return false;
    }
  }

  // Genre demotes = HARD FILTER (Spotify-grade: user says "no horror" = no horror)
  if (prefs.genreDemotes && prefs.genreDemotes.length > 0 && movie.genre_ids) {
    for (const gid of movie.genre_ids) {
      if (prefs.genreDemotes.includes(gid)) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Apply client-side preference filtering and reranking.
 * Spotify-grade semantics:
 * - Hard filters REMOVE items (runtime range, genre demotes)
 * - Soft deltas RERANK remaining items (era, genre boosts, intensity, popularity)
 * - Sort by (baseScore + delta) for stable, meaningful ordering
 */
function applyClientSidePrefsToShelves(
  shelves: ForYouShelfWithPro[],
  prefs: TastePreferences | null
): ForYouShelfWithPro[] {
  if (!prefs) return shelves;

  return shelves.map((shelf) => {
    // STEP 1: Apply hard filters FIRST (removes items)
    const filteredMovies = shelf.movies.filter((movie) => passesHardPrefs(movie, prefs));

    // STEP 2: Compute baseScore + delta for remaining items
    const rankedMovies = filteredMovies.map((movie, originalIndex) => {
      const baseScore = computeBaseScore(movie);
      let scoreDelta = 0;

      // Era preference: strong penalty for outside range
      if (prefs.era && movie.release_date) {
        const year = parseInt(movie.release_date.split('-')[0], 10);
        if (!isNaN(year)) {
          if (year >= prefs.era.min && year <= prefs.era.max) {
            scoreDelta += 5; // Small boost for in-range
          } else {
            scoreDelta -= 15; // Strong penalty for outside (-1.5 on 0-10 scale = -15 on 0-100)
          }
        }
      }

      // Genre boosts: DOMINANT positive (Spotify-grade: user picks show up prominently)
      // +30 to +50 on 0-100 scale (equivalent to +3.0 to +5.0 on 0-10)
      if (prefs.genreBoosts && prefs.genreBoosts.length > 0 && movie.genre_ids) {
        let boostCount = 0;
        for (const gid of movie.genre_ids) {
          if (prefs.genreBoosts.includes(gid)) {
            boostCount++;
          }
        }
        if (boostCount > 0) {
          // Base boost of 30, +10 per additional matching genre (caps at 50)
          scoreDelta += Math.min(30 + (boostCount - 1) * 10, 50);
        }
      }

      // Intensity preference (1-10): genre diversity proxy
      // Low intensity (1-3): boost comfort zone (boosted genres)
      // High intensity (8-10): boost exploration (non-boosted genres)
      if (prefs.intensity && movie.genre_ids && movie.genre_ids.length > 0) {
        const intensityScore = prefs.intensity;
        const userBoosts = prefs.genreBoosts || [];
        const hasBoostMatch = movie.genre_ids.some((gid) => userBoosts.includes(gid));

        if (intensityScore <= 3) {
          // Low intensity: prefer comfort zone
          if (hasBoostMatch) {
            scoreDelta += 10;
          } else {
            scoreDelta -= 5;
          }
        } else if (intensityScore >= 8) {
          // High intensity: prefer exploration
          if (!hasBoostMatch && userBoosts.length > 0) {
            scoreDelta += 10;
          } else if (hasBoostMatch) {
            scoreDelta -= 5;
          }
        }
        // Intensity 4-7: neutral
      }

      // Popularity preference (1=hidden gems, 10=mainstream)
      if (prefs.popularity && movie.popularity !== undefined) {
        const isMainstream = movie.popularity > 50;
        const prefersMainstream = prefs.popularity >= 6;
        if (isMainstream === prefersMainstream) {
          scoreDelta += 8;
        } else {
          scoreDelta -= 4;
        }
      }

      return { movie, baseScore, scoreDelta, originalIndex };
    });

    // STEP 3: Sort by (baseScore + delta) descending, then by original index (stable)
    rankedMovies.sort((a, b) => {
      const totalA = a.baseScore + a.scoreDelta;
      const totalB = b.baseScore + b.scoreDelta;
      const scoreDiff = totalB - totalA;
      if (scoreDiff !== 0) return scoreDiff;
      return a.originalIndex - b.originalIndex;
    });

    return {
      ...shelf,
      movies: rankedMovies.map((r) => r.movie),
    };
  });
}

// =============================================================================
// SKELETON COMPONENTS
// =============================================================================

function HeroSkeleton() {
  return (
    <div className="relative h-[500px] w-full overflow-hidden animate-pulse">
      <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-slate-800/40 to-slate-700/20" />
      <div className="absolute bottom-0 left-0 right-0 px-6 pb-12">
        <div className="max-w-2xl">
          <div className="h-6 w-32 bg-slate-700 rounded-full mb-4" />
          <div className="h-12 w-80 bg-slate-700 rounded mb-4" />
          <div className="h-5 w-64 bg-slate-700/60 rounded mb-6" />
          <div className="flex gap-3">
            <div className="h-12 w-32 bg-slate-700 rounded-lg" />
            <div className="h-12 w-28 bg-slate-700/60 rounded-lg" />
            <div className="h-12 w-28 bg-slate-700/60 rounded-lg" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ShelfSkeleton({ title }: { title?: string }) {
  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-4">
        {title ? (
          <h2 className="text-xl font-semibold text-white">{title}</h2>
        ) : (
          <div className="h-7 w-48 bg-slate-700 rounded animate-pulse" />
        )}
      </div>
      <div className="flex gap-4 overflow-hidden">
        {[...Array(7)].map((_, i) => (
          <div key={i} className="flex-shrink-0 w-[160px]">
            <div className="aspect-[2/3] bg-slate-800 rounded-lg animate-pulse" />
            <div className="mt-2 h-4 w-24 bg-slate-700 rounded animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// CAROUSEL COMPONENT
// =============================================================================

interface MovieCarouselProps {
  title: string;
  subtitle?: string;
  movies: MediaItem[];
  onMovieClick: (movie: MediaItem) => void;
  seeAllLink?: string;
  /** Number of items to load with high priority (first shelf, above fold) */
  highPriorityCount?: number;
}

function MovieCarousel({ title, subtitle, movies, onMovieClick, seeAllLink, highPriorityCount = 0 }: MovieCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Drag-to-scroll: use refs for pointer values (avoids stale closures in handlers)
  // Keep isDragging as state for cursor re-render, but mirror to ref for handler checks
  const [isDragging, setIsDragging] = useState(false);
  const isTrackingRef = useRef(false); // Tracking pointer but not yet dragging
  const isDraggingRef = useRef(false); // Actually dragging (past threshold)
  const hasCapturedRef = useRef(false); // Whether we've called setPointerCapture
  const pointerIdRef = useRef<number | null>(null);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const dragDistanceRef = useRef(0);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Pointer drag-to-scroll handlers
  // IMPORTANT: Don't capture pointer immediately - only after drag threshold exceeded.
  // This allows nested interactive elements (QuickActionButton) to receive clicks.
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!scrollRef.current) return;

    // Ignore if pointerdown originates from interactive element (buttons, links, etc.)
    const target = e.target as HTMLElement;
    if (target.closest('button, a, input, textarea, select, [role="button"], [role="menuitem"]')) {
      return;
    }

    // Start tracking but don't capture yet
    isTrackingRef.current = true;
    isDraggingRef.current = false;
    hasCapturedRef.current = false;
    pointerIdRef.current = e.pointerId;
    startXRef.current = e.clientX;
    scrollLeftRef.current = scrollRef.current.scrollLeft;
    dragDistanceRef.current = 0;
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isTrackingRef.current || !scrollRef.current) return;

    const dx = e.clientX - startXRef.current;
    dragDistanceRef.current = Math.abs(dx);

    // Only capture pointer and start dragging after threshold exceeded (3px)
    if (!hasCapturedRef.current && dragDistanceRef.current > 3) {
      try {
        scrollRef.current.setPointerCapture(e.pointerId);
        hasCapturedRef.current = true;
      } catch {
        // Pointer may have been released
      }
      isDraggingRef.current = true;
      setIsDragging(true);
    }

    // Apply scroll offset
    if (isDraggingRef.current) {
      scrollRef.current.scrollLeft = scrollLeftRef.current - dx;
    }
  };

  const handlePointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    isTrackingRef.current = false;
    isDraggingRef.current = false;
    setIsDragging(false);

    // Release pointer capture if we captured it
    if (hasCapturedRef.current && scrollRef.current) {
      try {
        scrollRef.current.releasePointerCapture(e.pointerId);
      } catch {
        // Browser quirks: pointerId may already be released
      }
    }
    hasCapturedRef.current = false;
    pointerIdRef.current = null;
  };

  // Prevent navigation clicks after drag (threshold: 5px)
  const handleMovieClick = (movie: MediaItem) => {
    if (dragDistanceRef.current > 5) {
      // Was a drag, suppress click
      return;
    }
    onMovieClick(movie);
  };

  if (movies.length === 0) return null;

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-white">{title}</h2>
          {subtitle && (
            <p className="text-sm text-slate-400 mt-0.5">{subtitle}</p>
          )}
        </div>
        {seeAllLink && (
          <Link to={seeAllLink} className="text-sm text-teal-400 hover:text-teal-300">
            See All &gt;
          </Link>
        )}
      </div>
      <div className="relative">
        {/* Left Arrow - stable opacity, brighten on arrow hover only */}
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-[#0A0A0A]/80 hover:bg-black rounded-full flex items-center justify-center text-white opacity-30 hover:opacity-100 transition-opacity -ml-2"
        >
          <ChevronLeft size={24} />
        </button>

        {/* Movies Row with drag-to-scroll */}
        <div
          ref={scrollRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerLeave={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          className={`flex gap-4 overflow-x-auto scrollbar-hide pb-2 select-none ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          style={{ touchAction: 'pan-y' }}
        >
          {movies.map((movie, index) => (
            <div key={movie.id} className="flex-shrink-0 w-[160px]">
              <MovieCard
                movie={movie}
                onClick={handleMovieClick}
                showQuickActions={true}
                imagePriority={index < highPriorityCount ? 'high' : 'low'}
              />
            </div>
          ))}
        </div>

        {/* Right Arrow - stable opacity, brighten on arrow hover only */}
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-[#0A0A0A]/80 hover:bg-black rounded-full flex items-center justify-center text-white opacity-30 hover:opacity-100 transition-opacity -mr-2"
        >
          <ChevronRight size={24} />
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// LOCKED SHELF ROW (Pro paywall for shelf items)
// =============================================================================

interface LockedShelfRowProps {
  title: string;
  subtitle?: string;
  /** Which tier unlocks this shelf. Defaults to 'pro'. */
  requiredTier?: 'social' | 'pro';
}

function LockedShelfRow({ title, subtitle, requiredTier = 'pro' }: LockedShelfRowProps) {
  const isSocialTier = requiredTier === 'social';
  const badgeLabel = isSocialTier ? 'Social' : 'Pro';
  const iconColor = isSocialTier ? 'text-teal-400' : 'text-purple-400';
  const badgeClasses = isSocialTier
    ? 'text-teal-400 bg-teal-500/20 border-teal-500/40'
    : 'text-purple-400 bg-purple-500/20 border-purple-500/40';
  const buttonClasses = isSocialTier
    ? 'from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400'
    : 'from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400';
  const ctaText = isSocialTier ? 'Unlock with Social' : 'Unlock with Pro';

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Lock size={16} className={iconColor} />
          <h2 className="text-xl font-semibold text-white">{title}</h2>
          <span className={`px-2 py-0.5 text-xs font-medium border rounded-full ${badgeClasses}`}>
            {badgeLabel}
          </span>
        </div>
      </div>
      {subtitle && (
        <p className="text-sm text-slate-400 mb-4">{subtitle}</p>
      )}
      <div className="relative">
        {/* Blurred placeholder cards */}
        <div className="flex gap-4 overflow-hidden">
          {[...Array(7)].map((_, i) => (
            <div key={i} className="flex-shrink-0 w-[160px]">
              <div className="aspect-[2/3] bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg blur-sm opacity-50" />
              <div className="mt-2 h-4 w-24 bg-slate-700 rounded blur-sm opacity-50" />
            </div>
          ))}
        </div>
        {/* Overlay with CTA */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
          <Link
            to="/pricing"
            className={`inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r ${buttonClasses} text-white rounded-lg font-semibold transition-all shadow-lg`}
          >
            <Wand2 size={18} />
            {ctaText}
          </Link>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// EMPTY SHELF ROW (shelf with no items or needs more)
// =============================================================================

interface EmptyShelfRowProps {
  title: string;
  subtitle?: string;
  message: string;
}

function EmptyShelfRow({ title, subtitle, message }: EmptyShelfRowProps) {
  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-white">{title}</h2>
          {subtitle && (
            <p className="text-sm text-slate-400 mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
      <div className="relative">
        {/* Faded placeholder cards */}
        <div className="flex gap-4 overflow-hidden opacity-20">
          {[...Array(7)].map((_, i) => (
            <div key={i} className="flex-shrink-0 w-[160px]">
              <div className="aspect-[2/3] bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg" />
              <div className="mt-2 h-4 w-24 bg-slate-700 rounded" />
            </div>
          ))}
        </div>
        {/* Overlay with message */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-lg px-6 py-4 text-center max-w-md">
            <p className="text-slate-300">{message}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// TASTE CONTROLS MODAL (with paywall preview for free users)
// =============================================================================

interface TasteControlsProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (prefs: TastePreferences) => void;
  initialPrefs: TastePreferences | null;
  isApplying: boolean;
  isPro: boolean;
}

function TasteControls({ isOpen, onClose, onApply, initialPrefs, isApplying, isPro }: TasteControlsProps) {
  const navigate = useNavigate();
  const [intensity, setIntensity] = useState(initialPrefs?.intensity ?? 5);
  const [popularity, setPopularity] = useState(initialPrefs?.popularity ?? 5);
  const [runtimeMin, setRuntimeMin] = useState(initialPrefs?.runtime?.min ?? 60);
  const [runtimeMax, setRuntimeMax] = useState(initialPrefs?.runtime?.max ?? 180);
  const [eraMin, setEraMin] = useState(initialPrefs?.era?.min ?? 1970);
  const [eraMax, setEraMax] = useState(initialPrefs?.era?.max ?? 2025);
  const [genreBoosts, setGenreBoosts] = useState<number[]>(initialPrefs?.genreBoosts ?? []);
  const [genreDemotes, setGenreDemotes] = useState<number[]>(initialPrefs?.genreDemotes ?? []);

  const isLocked = !isPro;

  if (!isOpen) return null;

  const toggleGenreBoost = (genreId: number) => {
    if (isLocked) return;
    setGenreBoosts((prev) =>
      prev.includes(genreId) ? prev.filter((id) => id !== genreId) : [...prev, genreId]
    );
    // Remove from demotes if it was there (mutually exclusive)
    setGenreDemotes((prev) => prev.filter((id) => id !== genreId));
  };

  const toggleGenreDemote = (genreId: number) => {
    if (isLocked) return;
    setGenreDemotes((prev) =>
      prev.includes(genreId) ? prev.filter((id) => id !== genreId) : [...prev, genreId]
    );
    // Remove from boosts if it was there (mutually exclusive)
    setGenreBoosts((prev) => prev.filter((id) => id !== genreId));
  };

  const handleApply = () => {
    // Free users cannot apply - route to pricing
    if (!isPro) {
      navigate('/pricing');
      onClose();
      return;
    }
    const prefs: TastePreferences = {
      intensity,
      popularity,
      runtime: { min: runtimeMin, max: runtimeMax },
      era: { min: eraMin, max: eraMax },
      genreBoosts: genreBoosts.length > 0 ? genreBoosts : undefined,
      genreDemotes: genreDemotes.length > 0 ? genreDemotes : undefined,
    };
    onApply(prefs);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-[#1A1F26] rounded-xl p-6 w-full max-w-md shadow-2xl border border-slate-700 relative max-h-[90vh] overflow-y-auto">
        {/* Locked overlay for free users */}
        {isLocked && (
          <div className="absolute inset-0 bg-black/60 rounded-xl z-10 flex flex-col items-center justify-center">
            <Lock size={32} className="text-purple-400 mb-3" />
            <p className="text-white font-semibold mb-2">Unlock Taste Controls</p>
            <p className="text-slate-400 text-sm mb-4 text-center px-4">
              Fine-tune your recommendations with Pro
            </p>
            <Link
              to="/pricing"
              onClick={onClose}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-lg font-semibold hover:from-purple-500 hover:to-purple-400 transition-all"
            >
              <Wand2 size={18} />
              Upgrade to Pro
            </Link>
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-white flex items-center gap-2">
            <SlidersHorizontal size={20} className="text-teal-400" />
            Taste Controls
            {isLocked && <Lock size={14} className="text-purple-400" />}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white relative z-20">
            <X size={24} />
          </button>
        </div>

        {/* Intensity Slider */}
        <div className="mb-6">
          <label className="block text-sm text-slate-300 mb-2">
            Intensity: {intensity === 1 ? 'Light & Fun' : intensity === 10 ? 'Heavy & Intense' : `${intensity}/10`}
          </label>
          <input
            type="range"
            min="1"
            max="10"
            value={intensity}
            onChange={(e) => !isLocked && setIntensity(parseInt(e.target.value, 10))}
            disabled={isLocked}
            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>Light & Fun</span>
            <span>Heavy & Intense</span>
          </div>
        </div>

        {/* Popularity Slider */}
        <div className="mb-6">
          <label className="block text-sm text-slate-300 mb-2">
            Popularity: {popularity === 1 ? 'Hidden Gems' : popularity === 10 ? 'Mainstream' : `${popularity}/10`}
          </label>
          <input
            type="range"
            min="1"
            max="10"
            value={popularity}
            onChange={(e) => !isLocked && setPopularity(parseInt(e.target.value, 10))}
            disabled={isLocked}
            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>Hidden Gems</span>
            <span>Mainstream</span>
          </div>
        </div>

        {/* Runtime Range */}
        <div className="mb-6">
          <label className="block text-sm text-slate-300 mb-2">
            Runtime: {runtimeMin}-{runtimeMax} min
          </label>
          <div className="flex gap-4">
            <div className="flex-1">
              <input
                type="number"
                min="30"
                max="600"
                value={runtimeMin}
                onChange={(e) => !isLocked && setRuntimeMin(Math.min(parseInt(e.target.value, 10) || 30, runtimeMax))}
                disabled={isLocked}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <span className="text-xs text-slate-500">Min</span>
            </div>
            <div className="flex-1">
              <input
                type="number"
                min="30"
                max="600"
                value={runtimeMax}
                onChange={(e) => !isLocked && setRuntimeMax(Math.max(parseInt(e.target.value, 10) || 180, runtimeMin))}
                disabled={isLocked}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <span className="text-xs text-slate-500">Max</span>
            </div>
          </div>
        </div>

        {/* Era Range */}
        <div className="mb-6">
          <label className="block text-sm text-slate-300 mb-2">
            Era: {eraMin}-{eraMax}
          </label>
          <div className="flex gap-4">
            <div className="flex-1">
              <input
                type="number"
                min="1900"
                max="2030"
                value={eraMin}
                onChange={(e) => !isLocked && setEraMin(Math.min(parseInt(e.target.value, 10) || 1970, eraMax))}
                disabled={isLocked}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <span className="text-xs text-slate-500">From</span>
            </div>
            <div className="flex-1">
              <input
                type="number"
                min="1900"
                max="2030"
                value={eraMax}
                onChange={(e) => !isLocked && setEraMax(Math.max(parseInt(e.target.value, 10) || 2025, eraMin))}
                disabled={isLocked}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <span className="text-xs text-slate-500">To</span>
            </div>
          </div>
        </div>

        {/* Genre Boosts - More of these */}
        <div className="mb-6">
          <label className="block text-sm text-slate-300 mb-2">
            <ThumbsUp size={14} className="inline mr-1 text-teal-400" />
            Show me more: {genreBoosts.length > 0 ? `${genreBoosts.length} selected` : 'None selected'}
          </label>
          <div className="flex flex-wrap gap-2">
            {UNIFIED_GENRES.map((genre) => (
              <button
                key={`boost-${genre.id}`}
                onClick={() => toggleGenreBoost(genre.id)}
                disabled={isLocked || genreDemotes.includes(genre.id)}
                className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                  genreBoosts.includes(genre.id)
                    ? 'bg-teal-500/30 border-teal-500 text-teal-300'
                    : genreDemotes.includes(genre.id)
                    ? 'bg-slate-800 border-slate-600 text-slate-500 cursor-not-allowed'
                    : 'bg-slate-800 border-slate-600 text-slate-300 hover:border-teal-500 hover:text-teal-300'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {genre.name}
              </button>
            ))}
          </div>
        </div>

        {/* Genre Demotes - Less of these */}
        <div className="mb-6">
          <label className="block text-sm text-slate-300 mb-2">
            <ThumbsDown size={14} className="inline mr-1 text-red-400" />
            Show me less: {genreDemotes.length > 0 ? `${genreDemotes.length} selected` : 'None selected'}
          </label>
          <div className="flex flex-wrap gap-2">
            {UNIFIED_GENRES.map((genre) => (
              <button
                key={`demote-${genre.id}`}
                onClick={() => toggleGenreDemote(genre.id)}
                disabled={isLocked || genreBoosts.includes(genre.id)}
                className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                  genreDemotes.includes(genre.id)
                    ? 'bg-red-500/30 border-red-500 text-red-300'
                    : genreBoosts.includes(genre.id)
                    ? 'bg-slate-800 border-slate-600 text-slate-500 cursor-not-allowed'
                    : 'bg-slate-800 border-slate-600 text-slate-300 hover:border-red-500 hover:text-red-300'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {genre.name}
              </button>
            ))}
          </div>
        </div>

        {/* Apply Button */}
        <button
          onClick={handleApply}
          disabled={isApplying}
          className="w-full py-3 bg-teal-500 text-white rounded-lg font-semibold hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isApplying ? 'Applying...' : 'Apply & Refresh'}
        </button>

        <p className="text-xs text-slate-500 text-center mt-3">
          Preferences expire after 24 hours
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// HERO CONTROLS (with paywall preview for free users)
// =============================================================================

interface HeroControlsProps {
  movie: MediaItem;
  reason: string | null;
  token?: string;
  isPro: boolean;
  onHideSuccess: (blockType: 'movie' | 'actor' | 'director' | 'genre', blockId: number) => void;
  onOpenTasteControls: () => void;
  onGenreTuneSuccess: () => void;
}

function HeroControls({ movie, reason, token, isPro, onHideSuccess, onOpenTasteControls, onGenreTuneSuccess }: HeroControlsProps) {
  const navigate = useNavigate();
  const { openWithQuestion } = useChat();
  const [isHideMenuOpen, setIsHideMenuOpen] = useState(false);
  const [isLoadingCredits, setIsLoadingCredits] = useState(false);
  const [credits, setCredits] = useState<{ director: CreditsPerson | null; topCast: CreditsPerson[] } | null>(null);
  const [isHiding, setIsHiding] = useState(false);

  const isLocked = !isPro;

  // Route to pricing for free users
  const goToPricing = () => {
    navigate('/pricing');
  };

  // Load credits when hide menu opens (for director/actor options)
  const handleOpenHideMenu = async () => {
    // Free users: show menu but actions will route to pricing
    setIsHideMenuOpen(true);
    if (!credits && !isLoadingCredits && isPro) {
      // Only fetch credits for Pro users (free users see static menu)
      setIsLoadingCredits(true);
      const result = await fetchCreditsForHero(movie.media_type || 'movie', movie.id, token);
      setCredits(result);
      setIsLoadingCredits(false);
    }
  };

  const handleHide = async (blockType: 'movie' | 'actor' | 'director', blockId: number, blockName: string) => {
    // Free users cannot hide - route to pricing
    if (!isPro) {
      setIsHideMenuOpen(false);
      goToPricing();
      return;
    }
    setIsHiding(true);
    try {
      await apiFetch('/for-you/tune', {
        token,
        method: 'POST',
        body: {
          action: 'add_block',
          block_type: blockType,
          block_id: blockId,
          block_name: blockName,
        },
      });
      setIsHideMenuOpen(false);
      onHideSuccess(blockType, blockId);
    } catch (e) {
      console.error('Failed to hide:', e);
    } finally {
      setIsHiding(false);
    }
  };

  const handleWhyThis = () => {
    // Free users cannot use Why this - route to pricing
    if (!isPro) {
      goToPricing();
      return;
    }
    const question = `Why did you recommend "${movie.title}"${reason ? ` with the reason: "${reason}"` : ''}? What makes this a good match for me?`;
    openWithQuestion(question);
    navigate('/filmply');
  };

  const handleMoreLikeThis = async () => {
    if (!isPro) {
      goToPricing();
      return;
    }
    const genreIds = movie.genre_ids ?? [];
    if (genreIds.length === 0) return;
    try {
      await apiFetch('/for-you/tune', {
        token,
        method: 'POST',
        body: {
          action: 'boost_genres',
          genre_ids: genreIds,
        },
      });
      onGenreTuneSuccess();
    } catch (e) {
      console.error('Failed to boost genres:', e);
    }
  };

  const handleLessLikeThis = async () => {
    if (!isPro) {
      goToPricing();
      return;
    }
    const genreIds = movie.genre_ids ?? [];
    if (genreIds.length === 0) return;
    try {
      await apiFetch('/for-you/tune', {
        token,
        method: 'POST',
        body: {
          action: 'demote_genres',
          genre_ids: genreIds,
        },
      });
      onGenreTuneSuccess();
    } catch (e) {
      console.error('Failed to demote genres:', e);
    }
  };

  return (
    <div className="flex items-center gap-2 mt-4">
      {/* Hide button with dropdown */}
      <div className="relative">
        <button
          onClick={handleOpenHideMenu}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-800/60 rounded-lg transition-colors"
        >
          <EyeOff size={16} />
          Hide
          {isLocked && <Lock size={12} className="text-purple-400 ml-1" />}
        </button>

        {isHideMenuOpen && (
          <>
            {/* Backdrop to close menu */}
            <div className="fixed inset-0 z-40" onClick={() => setIsHideMenuOpen(false)} />

            {/* Menu */}
            <div className="absolute left-0 bottom-full mb-2 w-56 bg-[#1A1F26] border border-slate-700 rounded-lg shadow-xl z-50 overflow-hidden">
              {isLocked && (
                <div className="px-4 py-2 text-xs text-purple-400 border-b border-slate-700 flex items-center gap-1">
                  <Lock size={12} />
                  Pro feature — tap to upgrade
                </div>
              )}
              <button
                onClick={() => handleHide('movie', movie.id, movie.title)}
                disabled={isHiding}
                className="w-full px-4 py-3 text-left text-sm text-white hover:bg-slate-700/50 transition-colors disabled:opacity-50"
              >
                Hide this movie
              </button>

              {isPro && isLoadingCredits ? (
                <div className="px-4 py-3 text-sm text-slate-500">Loading credits...</div>
              ) : isPro ? (
                <>
                  {credits?.director && (
                    <button
                      onClick={() => handleHide('director', credits.director!.id, credits.director!.name)}
                      disabled={isHiding}
                      className="w-full px-4 py-3 text-left text-sm text-white hover:bg-slate-700/50 transition-colors disabled:opacity-50"
                    >
                      Hide director: {credits.director.name}
                    </button>
                  )}

                  {credits?.topCast?.[0] && (
                    <button
                      onClick={() => handleHide('actor', credits.topCast[0].id, credits.topCast[0].name)}
                      disabled={isHiding}
                      className="w-full px-4 py-3 text-left text-sm text-white hover:bg-slate-700/50 transition-colors disabled:opacity-50"
                    >
                      Hide actor: {credits.topCast[0].name}
                    </button>
                  )}
                </>
              ) : (
                // Free users: show static options that route to pricing
                <>
                  <button
                    onClick={() => handleHide('director', 0, '')}
                    className="w-full px-4 py-3 text-left text-sm text-white hover:bg-slate-700/50 transition-colors"
                  >
                    Hide director
                  </button>
                  <button
                    onClick={() => handleHide('actor', 0, '')}
                    className="w-full px-4 py-3 text-left text-sm text-white hover:bg-slate-700/50 transition-colors"
                  >
                    Hide actor
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Why this? button */}
      <button
        onClick={handleWhyThis}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-800/60 rounded-lg transition-colors"
      >
        <HelpCircle size={16} />
        Why this?
        {isLocked && <Lock size={12} className="text-purple-400 ml-1" />}
      </button>

      {/* Taste Controls button */}
      <button
        onClick={onOpenTasteControls}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-800/60 rounded-lg transition-colors"
      >
        <SlidersHorizontal size={16} />
        Tune
        {isLocked && <Lock size={12} className="text-purple-400 ml-1" />}
      </button>

      {/* Genre tune actions */}
      <button
        onClick={handleMoreLikeThis}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-800/60 rounded-lg transition-colors"
      >
        <ThumbsUp size={16} />
        More like this
        {isLocked && <Lock size={12} className="text-purple-400 ml-1" />}
      </button>
      <button
        onClick={handleLessLikeThis}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-800/60 rounded-lg transition-colors"
      >
        <ThumbsDown size={16} />
        Less like this
        {isLocked && <Lock size={12} className="text-purple-400 ml-1" />}
      </button>
    </div>
  );
}

// =============================================================================
// HERO SECTION (Pro users or non-AI content)
// =============================================================================

interface HeroSectionProps {
  movie: MediaItem;
  reason: string | null;
  isColdStart: boolean;
  isAiGenerated: boolean;
  isPro: boolean;
  token?: string;
  onHideSuccess: (blockType: 'movie' | 'actor' | 'director' | 'genre', blockId: number) => void;
  onOpenTasteControls: () => void;
  onGenreTuneSuccess: () => void;
}

function HeroSection({ movie, reason, isColdStart, isAiGenerated, isPro, token, onHideSuccess, onOpenTasteControls, onGenreTuneSuccess }: HeroSectionProps) {
  const navigate = useNavigate();
  const { isInWatchlist, toggleWatchlist } = useUserLibrary();
  const year = movie.release_date?.split('-')[0];
  const mediaType = movie.media_type || 'movie';
  const detailRoute = mediaType === 'tv' ? `/tv/${movie.id}` : `/movie/${movie.id}`;
  const inWatchlist = isInWatchlist(movie.id, mediaType);

  const handleWatchlistClick = async () => {
    try {
      await toggleWatchlist(movie.id, mediaType, movie.title, movie.poster_path || undefined);
    } catch (error) {
      console.error('Failed to toggle watchlist:', error);
    }
  };

  return (
    <div className="relative h-[560px] w-full overflow-hidden">
      {/* Backdrop */}
      {movie.backdrop_path && (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://image.tmdb.org/t/p/original${movie.backdrop_path})`,
          }}
        />
      )}
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/60 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#111111]/80 to-transparent" />

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 px-6 pb-8">
        <div className="max-w-2xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-500/20 border border-teal-500 rounded-full mb-4">
            {isAiGenerated ? (
              <>
                <Wand2 size={14} className="text-purple-400" />
                <span className="text-sm text-purple-400 font-medium">Filmply Pick</span>
              </>
            ) : isColdStart ? (
              <>
                <Star size={14} className="text-teal-400" />
                <span className="text-sm text-teal-400 font-medium">Popular Pick</span>
              </>
            ) : (
              <>
                <Sparkles size={14} className="text-teal-400" />
                <span className="text-sm text-teal-400 font-medium">For You</span>
              </>
            )}
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
            {movie.title}
            {year && <span className="text-slate-400 font-normal ml-3">({year})</span>}
          </h1>

          {/* Personalized reason */}
          {reason && (
            <p className="text-slate-300 text-lg mb-4 max-w-xl">
              {reason}
            </p>
          )}

          {/* Ratings */}
          {(movie.imdb_rating || movie.rt_rating || movie.filmpl_rating) && (
            <div className="flex items-center gap-4 mb-4 text-sm">
              {movie.filmpl_rating && (
                <span className="text-purple-400">
                  <Star size={14} className="inline mr-1" />
                  {movie.filmpl_rating.toFixed(1)} Filmple
                </span>
              )}
              {movie.imdb_rating && (
                <span className="text-yellow-400">
                  {movie.imdb_rating.toFixed(1)} IMDb
                </span>
              )}
              {movie.rt_rating && (
                <span className="text-red-400">
                  {movie.rt_rating}% RT
                </span>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => navigate(detailRoute)}
              className="flex items-center gap-2 h-12 px-6 bg-teal-500 text-white rounded-lg font-semibold hover:bg-teal-600 transition-colors"
            >
              <Play size={20} className="fill-current" />
              Play Trailer
            </button>
            <button
              onClick={handleWatchlistClick}
              className={`flex items-center gap-2 h-12 px-6 border rounded-lg font-medium transition-colors ${
                inWatchlist
                  ? 'border-teal-500 text-teal-400 hover:bg-teal-500/10'
                  : 'border-slate-500 text-white hover:bg-[#1A1F26]'
              }`}
            >
              <Plus size={20} className={inWatchlist ? 'rotate-45' : ''} />
              {inWatchlist ? 'In Watchlist' : 'Watchlist'}
            </button>
            <Link
              to={detailRoute}
              className="flex items-center gap-2 h-12 px-6 border border-slate-500 text-white rounded-lg font-medium hover:bg-[#1A1F26] transition-colors"
            >
              <Info size={20} />
              More Info
            </Link>
          </div>

          {/* Hero controls (visible to all users, locked actions for free) */}
          <HeroControls
            movie={movie}
            reason={reason}
            token={token}
            isPro={isPro}
            onHideSuccess={onHideSuccess}
            onOpenTasteControls={onOpenTasteControls}
            onGenreTuneSuccess={onGenreTuneSuccess}
          />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// PAYWALLED HERO (Free users with AI-generated hero)
// =============================================================================

interface PaywalledHeroProps {
  movie: ForYouHeroRecommendation;
  reason: string | null;
  onOpenTasteControls: () => void;
}

function PaywalledHero({ movie, reason, onOpenTasteControls }: PaywalledHeroProps) {
  const navigate = useNavigate();

  // All control clicks route to pricing (free user)
  const goToPricing = () => navigate('/pricing');

  return (
    <div className="relative h-[560px] w-full overflow-hidden">
      {/* Blurred Backdrop */}
      {movie.backdrop_path && (
        <div
          className="absolute inset-0 bg-cover bg-center blur-xl scale-110"
          style={{
            backgroundImage: `url(https://image.tmdb.org/t/p/w780${movie.backdrop_path})`,
          }}
        />
      )}
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/70" />
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/40 to-transparent" />

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 px-6 pb-8">
        <div className="max-w-2xl">
          {/* Badge with Lock */}
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/20 border border-purple-500 rounded-full mb-4">
            <Lock size={14} className="text-purple-400" />
            <Wand2 size={14} className="text-purple-400" />
            <span className="text-sm text-purple-400 font-medium">Filmply Pick</span>
          </div>

          {/* Blurred Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 blur-md select-none">
            Hidden Movie Title
          </h1>

          {/* Teaser reason (visible) */}
          {reason && (
            <p className="text-slate-300 text-lg mb-4 max-w-xl">
              {reason}
            </p>
          )}

          {/* Upsell message */}
          <p className="text-slate-400 mb-6">
            Filmply finds your perfect match.
          </p>

          {/* Upgrade CTA */}
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 h-12 px-6 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-lg font-semibold hover:from-purple-500 hover:to-purple-400 transition-all"
          >
            <Wand2 size={20} />
            Unlock Filmply Picks — Pro
          </Link>

          {/* Locked controls preview (visible to show Pro value) */}
          <div className="flex items-center gap-2 mt-4">
            <button
              onClick={goToPricing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-800/60 rounded-lg transition-colors"
            >
              <EyeOff size={16} />
              Hide
              <Lock size={12} className="text-purple-400 ml-1" />
            </button>
            <button
              onClick={goToPricing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-800/60 rounded-lg transition-colors"
            >
              <HelpCircle size={16} />
              Why this?
              <Lock size={12} className="text-purple-400 ml-1" />
            </button>
            <button
              onClick={onOpenTasteControls}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-800/60 rounded-lg transition-colors"
            >
              <SlidersHorizontal size={16} />
              Tune
              <Lock size={12} className="text-purple-400 ml-1" />
            </button>
            <button
              onClick={goToPricing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-800/60 rounded-lg transition-colors"
            >
              <ThumbsUp size={16} />
              More like this
              <Lock size={12} className="text-purple-400 ml-1" />
            </button>
            <button
              onClick={goToPricing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-800/60 rounded-lg transition-colors"
            >
              <ThumbsDown size={16} />
              Less like this
              <Lock size={12} className="text-purple-400 ml-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// COLD START CTA
// =============================================================================

function ColdStartCTA() {
  return (
    <div className="bg-gradient-to-r from-teal-900/30 to-purple-900/30 border border-teal-500/30 rounded-xl p-6 mb-10">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-teal-500/20 rounded-lg">
          <Sparkles size={24} className="text-teal-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white mb-1">
            Get personalized recommendations
          </h3>
          <p className="text-slate-400 mb-4">
            Rate a few movies you've seen and we'll show you films you'll actually love.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-colors"
          >
            Start rating movies
          </Link>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// LOGGED OUT STATE
// =============================================================================

function LoggedOutState() {
  const goBack = useBackNavigation();

  return (
    <div className="min-h-screen bg-[#111111] relative overflow-hidden">
      <div className="absolute top-6 left-6 z-20">
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back</span>
        </button>
      </div>

      {/* Blurred background placeholder */}
      <div className="absolute inset-0 opacity-30 blur-xl">
        <div className="grid grid-cols-6 gap-4 p-8">
          {[...Array(18)].map((_, i) => (
            <div
              key={i}
              className="aspect-[2/3] bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg"
            />
          ))}
        </div>
      </div>

      {/* Overlay content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6 text-center">
        <div className="mb-6">
          <Lock size={48} className="text-teal-400 mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Your Personal Movie Feed
          </h1>
          <p className="text-slate-400 text-lg max-w-md">
            Recommendations tailored to your unique taste
          </p>
        </div>

        <Link
          to="/auth/callback"
          className="inline-flex items-center gap-2 px-8 py-3 bg-teal-500 text-white rounded-lg text-lg font-semibold hover:bg-teal-600 transition-colors mb-4"
        >
          Login to See Your Picks
        </Link>

        <p className="text-slate-500 text-sm">
          Sign up free — takes 30 seconds
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// HELPER: Convert API hero to MediaItem
// =============================================================================

function heroToMediaItem(hero: ForYouHeroRecommendation): MediaItem {
  return {
    id: hero.id,
    title: hero.title,
    overview: '',
    poster_path: hero.poster_path,
    backdrop_path: hero.backdrop_path,
    release_date: hero.release_date || '',
    genre_ids: hero.genre_ids ?? [],
    vote_average: hero.vote_average,
    vote_count: hero.vote_count ?? 0,
    popularity: hero.popularity ?? 0,
    original_language: '',
    media_type: hero.media_type,
    imdb_rating: hero.imdb_rating ?? undefined,
    rt_rating: hero.rt_rating ?? undefined,
    filmpl_rating: hero.filmpl_rating ?? undefined,
    runtime: hero.runtime ?? undefined,
  };
}

// =============================================================================
// AUTHENTICATED PAGE COMPONENT (inner)
// =============================================================================

/**
 * ForYouPageAuthed - Only rendered when user is authenticated.
 * Phase 2.1: Fetches from /api/for-you with paywall gating.
 * Phase 5.0: Adds hide controls, taste controls, Why this?, client-side reranking.
 */
function ForYouPageAuthed() {
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const queryClient = useQueryClient();
  const { session, profile } = useAuth();
  const { mediaType } = useMediaType();
  const { flushNow } = usePersistFlush();

  // Test user tier override: use effectiveIsPro for all UI gating
  const isPro = effectiveIsPro(profile);
  const tier = effectiveTier(profile);

  // Phase 5.0: Taste controls state
  const [isTasteControlsOpen, setIsTasteControlsOpen] = useState(false);
  const [localPrefs, setLocalPrefs] = useState<TastePreferences | null>(null);
  const [isApplyingPrefs, setIsApplyingPrefs] = useState(false);

  // Gate: API call only fires when authenticated
  const apiEnabled = !!session?.access_token && !!profile?.id;

  // Phase 2.0: Fetch from /api/for-you (backend cache)
  // Query key includes tier + mediaType to isolate cache per scope
  // Signal passed for cancellation on tier/media toggle
  const apiQuery = useQuery({
    queryKey: queryKeys.forYou.api(profile?.id || 'anon', tier, mediaType),
    queryFn: async ({ signal }): Promise<ForYouApiResponse> => {
      return apiFetch<ForYouApiResponse>(`/for-you?media_type=${mediaType}`, {
        token: session?.access_token,
        signal,
      });
    },
    enabled: apiEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 1, // Only retry once, then fallback
  });

  // Is this a degraded timeout payload? (popular-only fallback, not the real cache)
  const isDegradedPayload = apiQuery.data?.fallbackReason && !apiQuery.data?.cacheHit;

  // Persistence fix: Immediately flush cache to IndexedDB when For You data loads
  // SKIP flush for degraded timeout payloads — they must never be persisted
  useEffect(() => {
    if (apiQuery.isSuccess && apiQuery.data && !isDegradedPayload) {
      flushNow();
    }
  }, [apiQuery.isSuccess, apiQuery.data, isDegradedPayload, flushNow]);

  // Self-heal: if API returned a degraded timeout payload, schedule a single refetch
  // after 2 seconds so the UI converges to the real cache once regen completes
  const selfHealFired = useRef(false);
  useEffect(() => {
    if (isDegradedPayload && !selfHealFired.current) {
      selfHealFired.current = true;
      const timer = setTimeout(() => {
        apiQuery.refetch();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isDegradedPayload, apiQuery]);

  // Fallback gating: run Phase 1 queries when API is unavailable, failed, or returned empty shelves
  // This prevents empty shelf states when backend returns hero but no shelves (cache/tier edge cases)
  const apiShelvesEmpty = apiQuery.isFetched && (apiQuery.data?.shelves?.length ?? 0) === 0;
  const fallbackEnabled = !apiEnabled || apiQuery.isError || (apiQuery.isFetched && apiQuery.data?.hero == null) || apiShelvesEmpty;

  // Phase 1 fallback: algorithmic shelves (used when API fails or hero null)
  const fallbackQuery = useForYouShelves(fallbackEnabled);

  const handleMovieClick = (movie: MediaItem) => {
    const route = movie.media_type === 'tv' ? `/tv/${movie.id}` : `/movie/${movie.id}`;
    navigate(route);
  };

  // Phase 5.0: Handle hide success → optimistic update + regenerate + refetch
  const handleHideSuccess = useCallback(async (
    blockType: 'movie' | 'actor' | 'director' | 'genre',
    blockId: number
  ) => {
    // OPTIMISTIC UPDATE: For movie blocks, remove from cache immediately
    // This makes "Hide movie" feel instant without waiting for regen
    if (blockType === 'movie') {
      queryClient.setQueryData<ForYouApiResponse>(
        queryKeys.forYou.api(profile?.id || 'anon', tier, mediaType),
        (old) => {
          if (!old) return old;

          // Filter blocked movie from all shelves
          const filteredShelves = old.shelves.map((shelf) => ({
            ...shelf,
            items: shelf.items.filter((item) => item.id !== blockId),
          }));

          // Check if hero needs replacement
          let updatedHero = old.hero;
          if (old.hero?.recommendation?.id === blockId) {
            // Find first available item from filtered shelves as replacement
            const firstShelfWithItems = filteredShelves.find((s) => s.items.length > 0);
            if (firstShelfWithItems && firstShelfWithItems.items[0]) {
              const replacementItem = firstShelfWithItems.items[0];
              updatedHero = {
                ...old.hero,
                recommendation: replacementItem,
                reason: 'Updated after hiding a title',
                isAiGenerated: false,
              };
            }
            // If no replacement found, keep hero unchanged (don't set to null)
          }

          return {
            ...old,
            hero: updatedHero,
            shelves: filteredShelves,
          };
        }
      );
    }

    try {
      // Trigger backend regeneration (non-blocking for UX)
      await apiFetch('/for-you/regenerate', {
        token: session?.access_token,
        method: 'POST',
      });
      // Invalidate all tiers (base key) and refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.forYou.apiBase(profile?.id || 'anon') });
    } catch (e) {
      console.error('Failed to regenerate after hide:', e);
    }
  }, [session?.access_token, profile?.id, tier, mediaType, queryClient]);

  // Handle genre tune success (More/Less like this) → regenerate + refetch
  const handleGenreTuneSuccess = useCallback(async () => {
    try {
      await apiFetch('/for-you/regenerate', {
        token: session?.access_token,
        method: 'POST',
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.forYou.apiBase(profile?.id || 'anon') });
    } catch (e) {
      console.error('Failed to regenerate after genre tune:', e);
    }
  }, [session?.access_token, profile?.id, queryClient]);

  // Phase 5.0: Handle taste preferences apply
  const handleApplyPreferences = useCallback(async (prefs: TastePreferences) => {
    setIsApplyingPrefs(true);
    try {
      // 1. Save preferences to backend
      await apiFetch('/for-you/preferences', {
        token: session?.access_token,
        method: 'POST',
        body: { preferences: prefs },
      });

      // 2. Update local prefs for instant client-side reshuffle
      setLocalPrefs(prefs);

      // 3. Trigger backend regeneration
      await apiFetch('/for-you/regenerate', {
        token: session?.access_token,
        method: 'POST',
      });

      // 4. Invalidate all tiers (base key) and refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.forYou.apiBase(profile?.id || 'anon') });

      // 5. Close modal
      setIsTasteControlsOpen(false);
    } catch (e) {
      console.error('Failed to apply preferences:', e);
    } finally {
      setIsApplyingPrefs(false);
    }
  }, [session?.access_token, profile?.id, queryClient]);

  // Determine data source
  const apiData = apiQuery.data;
  const apiHero = apiData?.hero;
  const apiShelves = apiData?.shelves || [];

  // Use API hero if available, otherwise fallback to Phase 1
  const useApiHero = apiHero !== null && apiHero !== undefined;
  const fallbackHero = fallbackQuery.data?.hero;
  const fallbackReason = fallbackQuery.data?.heroReason;

  // Loading state
  const isLoading = apiQuery.isLoading || (!useApiHero && fallbackQuery.isLoading);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#111111]">
        <div className="px-6 pt-6">
          <button
            onClick={goBack}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Back</span>
          </button>
        </div>
        <HeroSkeleton />
        <div className="px-6 py-8">
          <ShelfSkeleton title="Loading your recommendations..." />
          <ShelfSkeleton />
          <ShelfSkeleton />
        </div>
      </div>
    );
  }

  // Determine cold start state
  const isColdStart = apiData?.isColdStart ?? fallbackQuery.isColdStart;

  // Render hero based on entitlement
  // FREE USERS: Always show PaywalledHero (blurred + CTA) regardless of hero source
  // PRO USERS: Show normal HeroSection with full access
  // NOTE: renderHero is called AFTER compliantHero is computed below (line ~1863)
  const renderHero = () => {
    // Use compliantHero which already has hard prefs enforcement applied
    const { hero, reason, isApiHero } = compliantHero;

    // No hero available
    if (!hero) {
      return <HeroSkeleton />;
    }

    // Convert hero to ForYouHeroRecommendation format for PaywalledHero
    // compliantHero.hero can be either ForYouHeroRecommendation or MediaItem
    const heroAsRecommendation: ForYouHeroRecommendation = {
      id: hero.id,
      title: hero.title,
      poster_path: hero.poster_path ?? null,
      backdrop_path: hero.backdrop_path ?? null,
      release_date: hero.release_date ?? null,
      vote_average: hero.vote_average ?? 0,
      media_type: (hero.media_type as 'movie' | 'tv') || 'movie',
      filmpl_rating: hero.filmpl_rating ?? undefined,
      imdb_rating: hero.imdb_rating ?? undefined,
      rt_rating: hero.rt_rating ?? undefined,
      popularity: hero.popularity ?? undefined,
      runtime: hero.runtime ?? undefined,
      genre_ids: hero.genre_ids ?? undefined,
      vote_count: hero.vote_count ?? undefined,
    };

    // Convert to MediaItem format for HeroSection (using heroToMediaItem pattern)
    const heroAsMediaItem: MediaItem = {
      id: hero.id,
      title: hero.title,
      overview: '', // Not needed for hero display
      poster_path: hero.poster_path ?? null,
      backdrop_path: hero.backdrop_path ?? null,
      release_date: hero.release_date ?? '',
      vote_average: hero.vote_average ?? 0,
      vote_count: hero.vote_count ?? 0,
      popularity: hero.popularity ?? 0,
      original_language: 'en', // Default
      media_type: (hero.media_type as MediaType) || 'movie',
      genre_ids: hero.genre_ids ?? [],
      runtime: hero.runtime ?? undefined,
      filmpl_rating: hero.filmpl_rating ?? undefined,
      imdb_rating: hero.imdb_rating ?? undefined,
      rt_rating: hero.rt_rating ?? undefined,
    };

    // Free user: ALWAYS show paywalled hero
    if (!isPro) {
      return (
        <PaywalledHero
          movie={heroAsRecommendation}
          reason={reason}
          onOpenTasteControls={() => setIsTasteControlsOpen(true)}
        />
      );
    }

    // Pro user: show normal hero with full access
    // Determine cold start and AI generated flags from original API hero if available
    const originalApiHero = useApiHero && apiHero ? apiHero : null;
    const isColdStartHero = isApiHero && originalApiHero ? originalApiHero.type === 'cold_start' : fallbackQuery.isColdStart;
    const isAiGeneratedHero = isApiHero && originalApiHero ? originalApiHero.isAiGenerated === true : false;

    return (
      <HeroSection
        movie={heroAsMediaItem}
        reason={reason}
        isColdStart={isColdStartHero}
        isAiGenerated={isAiGeneratedHero}
        isPro={isPro}
        token={session?.access_token}
        onHideSuccess={handleHideSuccess}
        onOpenTasteControls={() => setIsTasteControlsOpen(true)}
        onGenreTuneSuccess={handleGenreTuneSuccess}
      />
    );
  };

  // Combine shelves: prefer API shelves, fallback to Phase 1
  // Preserve isPro field from API shelves for paywall gating
  const usingFallbackShelves = apiShelves.length === 0;
  const rawShelves: ForYouShelfWithPro[] = !usingFallbackShelves
    ? apiShelves.map((shelf): ForYouShelfWithPro => ({
        id: shelf.id,
        title: shelf.title,
        subtitle: shelf.subtitle,
        movies: shelf.items.map(heroToMediaItem),
        anchor: shelf.anchor ? heroToMediaItem({ ...shelf.anchor, poster_path: null, backdrop_path: null, release_date: null, vote_average: 0, media_type: 'movie' }) : undefined,
        shelfType: shelf.isAiGenerated ? 'similarity' : 'discovery',
        isPro: shelf.isPro, // Preserve isPro for paywall gating
        isSocial: shelf.isSocial, // Preserve isSocial for social shelf gating
        state: shelf.state, // Shelf state for empty/needs_more handling
        empty_message: shelf.empty_message, // Message to show for empty shelves
      }))
    : fallbackQuery.data?.shelves || [];

  // Track if we're showing fallback shelves because API returned empty (for user messaging)
  const showingFallbackDueToEmptyApi = apiShelvesEmpty && usingFallbackShelves && rawShelves.length > 0;

  // Phase 5.0: Apply client-side preference reranking for instant visual feedback
  const shelvesToRender = applyClientSidePrefsToShelves(rawShelves, localPrefs);

  // Phase 5.1: Spotify-grade hero hard prefs enforcement
  // Hero must respect same hard prefs (runtime range, genre demotes) as shelves
  // If hero violates hard prefs, replace with first eligible item from filtered shelves
  const getHardPrefsCompliantHero = (): {
    hero: ForYouHeroRecommendation | MediaItem | null;
    reason: string | null;
    isApiHero: boolean;
    isFallback: boolean;
  } => {
    // Resolve current hero: API hero takes precedence, then fallback
    const resolvedApiHero = useApiHero && apiHero ? apiHero : null;
    const resolvedFallbackHero = fallbackHero ?? null;

    // Helper: convert hero recommendation to MediaItem for passesHardPrefs check
    const heroAsMediaItem = (hero: ForYouHeroRecommendation): MediaItem => ({
      id: hero.id,
      title: hero.title,
      overview: '',
      poster_path: hero.poster_path ?? null,
      backdrop_path: hero.backdrop_path ?? null,
      release_date: hero.release_date ?? '',
      vote_average: hero.vote_average ?? 0,
      vote_count: hero.vote_count ?? 0,
      popularity: hero.popularity ?? 0,
      original_language: 'en',
      media_type: (hero.media_type as MediaType) || 'movie',
      genre_ids: hero.genre_ids ?? [],
      runtime: hero.runtime ?? undefined,
      filmpl_rating: hero.filmpl_rating ?? undefined,
      imdb_rating: hero.imdb_rating ?? undefined,
      rt_rating: hero.rt_rating ?? undefined,
    });

    // Helper: find replacement from filtered shelves (prefer item with backdrop_path)
    const findReplacementFromShelves = (): MediaItem | null => {
      // First pass: find item with backdrop_path
      for (const shelf of shelvesToRender) {
        for (const movie of shelf.movies) {
          if (movie.backdrop_path) {
            return movie;
          }
        }
      }
      // Second pass: any item
      for (const shelf of shelvesToRender) {
        if (shelf.movies.length > 0) {
          return shelf.movies[0];
        }
      }
      return null;
    };

    // Check API hero first
    if (resolvedApiHero) {
      const heroItem = heroAsMediaItem(resolvedApiHero.recommendation);
      // If localPrefs exists and hero fails hard prefs, find replacement
      if (localPrefs && !passesHardPrefs(heroItem, localPrefs)) {
        const replacement = findReplacementFromShelves();
        if (replacement) {
          return {
            hero: replacement,
            reason: 'Updated based on your preferences',
            isApiHero: false,
            isFallback: false,
          };
        }
        // No replacement available - use original (edge case: all shelves empty)
      }
      return {
        hero: resolvedApiHero.recommendation,
        reason: resolvedApiHero.reason,
        isApiHero: true,
        isFallback: false,
      };
    }

    // Check fallback hero
    if (resolvedFallbackHero) {
      // If localPrefs exists and hero fails hard prefs, find replacement
      if (localPrefs && !passesHardPrefs(resolvedFallbackHero, localPrefs)) {
        const replacement = findReplacementFromShelves();
        if (replacement) {
          return {
            hero: replacement,
            reason: 'Updated based on your preferences',
            isApiHero: false,
            isFallback: false,
          };
        }
      }
      return {
        hero: resolvedFallbackHero,
        reason: fallbackReason || null,
        isApiHero: false,
        isFallback: true,
      };
    }

    return { hero: null, reason: null, isApiHero: false, isFallback: false };
  };

  const compliantHero = getHardPrefsCompliantHero();

  return (
    <div className="min-h-screen bg-[#111111] relative">
      <div className="absolute top-20 left-6 z-20">
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back</span>
        </button>
      </div>

      {/* Hero Section */}
      {renderHero()}

      {/* Shelves */}
      <div className="px-6 py-8">
        {/* Cold start CTA */}
        {isColdStart && <ColdStartCTA />}

        {/* Note when showing fallback shelves due to empty API response */}
        {showingFallbackDueToEmptyApi && !isColdStart && (
          <div className="flex items-center gap-2 mb-6 text-sm text-slate-400">
            <Sparkles size={14} className="text-teal-400" />
            <span>Refreshing your personalized picks. Rate movies to improve recommendations.</span>
          </div>
        )}

        {/* Render shelves with Pro paywall gating + empty shelf handling */}
        {shelvesToRender.map((shelf: ForYouShelfWithPro, shelfIndex: number) => {
          // Pro-locked shelf: show LockedShelfRow if user is not Pro
          if (shelf.isPro && !isPro) {
            return (
              <LockedShelfRow
                key={shelf.id}
                title={shelf.title}
                subtitle={shelf.subtitle}
                requiredTier="pro"
              />
            );
          }

          // Social shelf hydration guard: hide until profile is known
          // Prevents locked-flash for Social/Pro users during boot
          if (shelf.isSocial && !profile) {
            return null;
          }

          // Social-locked shelf: show LockedShelfRow if user is Free
          if (shelf.isSocial && tier === 'free') {
            return (
              <LockedShelfRow
                key={shelf.id}
                title={shelf.title}
                subtitle={shelf.subtitle}
                requiredTier="social"
              />
            );
          }

          // Empty shelf: show EmptyShelfRow with message
          // Handles both 'empty' (0 items) and 'needs_more' (< 4 items with empty_message)
          if (shelf.movies.length === 0 && shelf.empty_message) {
            return (
              <EmptyShelfRow
                key={shelf.id}
                title={shelf.title}
                subtitle={shelf.subtitle}
                message={shelf.empty_message}
              />
            );
          }

          // Skip shelves with 0 items and no message (shouldn't happen, but safety)
          if (shelf.movies.length === 0) {
            return null;
          }

          // Normal shelf (free or user is Pro)
          // First shelf gets high priority for first 6 items (above-the-fold)
          // For 'needs_more' shelves with items, show the MovieCarousel + message below
          return (
            <div key={shelf.id}>
              <MovieCarousel
                title={shelf.title}
                subtitle={shelf.subtitle}
                movies={shelf.movies}
                onMovieClick={handleMovieClick}
                highPriorityCount={shelfIndex === 0 ? 6 : 0}
              />
              {/* Show message below carousel for 'needs_more' state */}
              {shelf.state === 'needs_more' && shelf.empty_message && (
                <p className="text-sm text-slate-500 italic -mt-6 mb-10 px-1">
                  {shelf.empty_message}
                </p>
              )}
            </div>
          );
        })}

        {/* Pro preview shelves for free users (paywall incentive) */}
        {/* Only render preview rows if that shelf ID is NOT already in shelvesToRender (prevents duplicates) */}
        {!isPro && !isLoading && (
          <>
            {!shelvesToRender.some(s => s.id === 'hidden_gems') && (
              <LockedShelfRow
                key="pro-preview-hidden-gems"
                title="Hidden gems you'll actually like"
                subtitle="Highly-rated films most people haven't seen"
              />
            )}
            {!shelvesToRender.some(s => s.id === 'one_step_outside') && (
              <LockedShelfRow
                key="pro-preview-step-outside"
                title="1 step outside your usual"
                subtitle="Films that expand your taste just enough"
              />
            )}
            {!shelvesToRender.some(s => s.id === 'short_and_great') && (
              <LockedShelfRow
                key="pro-preview-short-great"
                title="Short & great"
                subtitle="Perfect when you don't have much time"
              />
            )}
            {!shelvesToRender.some(s => s.id === 'tonight_mood') && (
              <LockedShelfRow
                key="pro-preview-tonight"
                title="Tonight: Chill"
                subtitle="Low-stress picks for your current mood"
              />
            )}
          </>
        )}

        {/* Empty state */}
        {shelvesToRender.length === 0 && !isLoading && (
          <div className="text-center py-20">
            <p className="text-slate-400 text-lg mb-4">
              We're building your recommendations...
            </p>
            <Link
              to="/"
              className="text-teal-400 hover:text-teal-300"
            >
              Browse movies while you wait
            </Link>
          </div>
        )}
      </div>

      {/* Phase 5.0: Taste Controls Modal (visible to all, locked for free users) */}
      <TasteControls
        isOpen={isTasteControlsOpen}
        onClose={() => setIsTasteControlsOpen(false)}
        onApply={handleApplyPreferences}
        initialPrefs={localPrefs}
        isApplying={isApplyingPrefs}
        isPro={isPro}
      />
    </div>
  );
}

// =============================================================================
// MAIN PAGE COMPONENT (outer - auth gate)
// =============================================================================

/**
 * ForYouPage - Entry point with auth gate.
 * Renders LoggedOutState for unauthenticated users (zero network calls).
 * Renders ForYouPageAuthed for authenticated users (triggers API + fallback loading).
 */
export function ForYouPage() {
  const { user } = useAuth();

  // Auth gate: logged-out users see LoggedOutState, no hooks called
  if (!user) {
    return <LoggedOutState />;
  }

  // Authenticated: render inner component
  return <ForYouPageAuthed />;
}
